// Sprint 12 — FE types aligned with the real BE shapes from
// `src/types/api.ts`. Keep this file BE-shape-faithful (no synthetic enums
// that the BE doesn't return).

// ---- Redirects -------------------------------------------------------------

/** HTTP status codes the BE accepts for redirects. 307 is included on the
 *  FE for completeness even though the seed only ships 301/302/308; the BE
 *  field is `int32` and validates the value range, not a fixed list. */
export const REDIRECT_STATUS_CODES = [301, 302, 307, 308] as const;
export type RedirectStatusCode = (typeof REDIRECT_STATUS_CODES)[number];

// Status labels are i18n-resolved at call sites via `web:redirect.status.label.<code>`.

export const REDIRECT_STATUS_COLOR: Record<RedirectStatusCode, string> = {
  301: 'blue',
  302: 'gold',
  307: 'cyan',
  308: 'purple',
};

export interface RedirectRule {
  id: string;
  fromPath: string;
  toPath: string;
  statusCode: number;
  isActive: boolean;
  hitCount: number;
  lastHitAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  rowVersion: string | null;
}

export interface RedirectListQuery {
  search?: string;
  isActive?: boolean;
  statusCode?: number;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  descending?: boolean;
}

export interface CreateRedirectInput {
  fromPath: string;
  toPath: string;
  statusCode: number;
  isActive: boolean;
  notes?: string | null;
}

export type UpdateRedirectInput = CreateRedirectInput;

// ---- OG preview (ISSUE-42) -------------------------------------------------

/**
 * Wire integer matches the BE `OgPreviewContentType` enum
 * (`0=Article, 1=Page, 3=Sss`). Kept as a const-tuple-shaped
 * object so call-sites read the named value but the JSON body stays an int.
 */
export const OG_PREVIEW_CONTENT_TYPES = {
  Article: 0,
  Page: 1,
  Sss: 3,
} as const;
export type OgPreviewContentTypeName = keyof typeof OG_PREVIEW_CONTENT_TYPES;
export type OgPreviewContentType =
  (typeof OG_PREVIEW_CONTENT_TYPES)[OgPreviewContentTypeName];

// Content-type labels are i18n-resolved at call sites via
// `web:og.contentType.<name>`.

export interface OgPreviewInput {
  type: OgPreviewContentType;
  slug: string;
}

// ---- SEO / Robots settings (MOD-08) ----------------------------------------

/**
 * AppSetting keys driving the public `robots.txt` / sitemap surface. Mirror the
 * BE `SettingKeys` (robots.*); read and written through the generic settings
 * API (category `seo`, private → not exposed on /public/settings).
 */
export const SEO_SETTING_KEYS = {
  allowIndexingGlobal: 'robots.allowIndexingGlobal',
  customBody: 'robots.customBody',
  sitemapUrl: 'robots.sitemapUrl',
} as const;

export type SeoSettingKey = (typeof SEO_SETTING_KEYS)[keyof typeof SEO_SETTING_KEYS];

export interface OgPreview {
  title: string;
  description: string;
  image: string | null;
  canonical: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string | null;
  ogType: string;
  ogUrl: string;
  twitterCard: string;
  twitterTitle: string;
  twitterDescription: string;
  twitterImage: string | null;
}
